import React from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  TrendingUp, 
  Users, 
  Zap,
  Heart,
  BarChart3
} from 'lucide-react';

interface Benefit {
  icon: React.ElementType;
  title: string;
  description: string;
  metric: string;
}

const benefits: Benefit[] = [
  {
    icon: Clock,
    title: "Acelere o atendimento",
    description: "Reduza o tempo de espera e libere mesas mais rapidamente",
    metric: "10 min mais rápido"
  },
  {
    icon: TrendingUp,
    title: "Aumente o ticket médio",
    description: "Sugestões inteligentes e upsell automático durante o pedido",
    metric: "+25% ticket médio"
  },
  {
    icon: Users,
    title: "Reduza a carga da equipe",
    description: "Libere sua equipe para focar no que realmente importa",
    metric: "-40% chamadas mesa"
  },
  {
    icon: Zap,
    title: "Pedidos sem erros",
    description: "Elimine erros de comunicação entre cliente e cozinha",
    metric: "99% precisão"
  },
  {
    icon: Heart,
    title: "Fidelização inteligente",
    description: "Programas de pontos e recompensas integrados",
    metric: "+35% retorno"
  },
  {
    icon: BarChart3,
    title: "Dados em tempo real",
    description: "Analytics completo para decisões baseadas em dados",
    metric: "Dashboard completo"
  }
];

const Benefits: React.FC = () => {
  return (
    <section className="mt-12 py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Benefícios que transformam seu negócio
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Descubra como o TAPEAT pode otimizar suas operações e melhorar a experiência dos seus clientes
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
            >
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                  <benefit.icon className="w-6 h-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {benefit.description}
                  </p>
                  <div className="inline-flex items-center px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm font-semibold">
                    {benefit.metric}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-lg text-gray-600 mb-6">
            Junte-se a mais de 1.000 restaurantes que já transformaram seu atendimento
          </p>
          <button className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors">
            Quero transformar meu restaurante
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Benefits;